import { QueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { fetchWithError, useParsedQueries, useParsedQuery } from "@nens/react-query-utils";
import { combineUrlAndParams } from "@nens/shared-utils";

import { lizardDateSchema } from "../dates";
import { geometrySchema } from "../geojson";
import { paginatedSchema } from "../pagination";

import { organisationSchema } from "./organisations";

export const rasterAlarmListItemSchema = z.object({
  url: z.string(),
  uuid: z.string(),
  name: z.string(),
  organisation: organisationSchema,
  active: z.boolean(),
  repeat_triggers: z.boolean(),
  snooze_sign_on: z.number(),
  snooze_sign_off: z.number(),
  comparison: z.string() /* < or >*/,
  relative_start: z.string().nullable(),
  relative_end: z.string().nullable(),
  messages: z.array(
    z.object({
      message: z.string(),
      contact_group: z.string(),
    })
  ),
  history: z.string().nullable(),
  thresholds: z.array(
    z.object({
      value: z.number(),
      warning_level: z.string(),
    })
  ),
  raster: z.string(),
  geometry: geometrySchema,
  latest_trigger: z.object({
    id: z.number().nullable(),
    trigger_time: lizardDateSchema,
    value: z.number().nullable(),
    value_time: lizardDateSchema,
    warning_level: z.string().nullable(),
    threshold_value: z.number().nullable(),
    threshold_time: lizardDateSchema.nullable(),
    first_time: lizardDateSchema.nullable(),
    message_sent: z.boolean(),
  }),
  created: lizardDateSchema,
  last_modified: lizardDateSchema.nullable(),
});

export type RasterAlarmListItem = z.infer<typeof rasterAlarmListItemSchema>;

export const rasterAlarmListSchema = paginatedSchema(rasterAlarmListItemSchema);
export type RasterAlarmList = z.infer<typeof rasterAlarmListSchema>;

export const rasterAlarmSchema = rasterAlarmListItemSchema;
export type RasterAlarm = RasterAlarmListItem;

export function useRasterAlarms(params = {}) {
  const url = combineUrlAndParams("/api/v4/rasteralarms/", params);
  return useParsedQuery<RasterAlarmList>(
    ["lizard-api-v4", "rasteralarms", url],
    () => fetchWithError(url),
    (json: unknown) => rasterAlarmListSchema.parse(json)
  );
}

export function setRasterAlarmsCache(
  queryClient: QueryClient,
  alarms: RasterAlarmList,
  params = {}
) {
  const url = combineUrlAndParams("/api/v4/rasteralarms/", params);

  queryClient.setQueryData(["lizard-api-v4", "rasteralarms", url], alarms);
}

export function useRasterAlarm(uuid: string) {
  return useParsedQuery<RasterAlarm>(
    ["lizard-api-v4", "rasteralarms", uuid],
    () => fetchWithError(`/api/v4/rasteralarms/${uuid}/`),
    (json: unknown) => rasterAlarmSchema.parse(json)
  );
}

export function setRasterAlarmCache(queryClient: QueryClient, alarm: RasterAlarm) {
  queryClient.setQueryData(["lizard-api-v4", "rasteralarms", alarm.uuid], alarm);
}

export function useMultipleRasterAlarms(uuids: string[]) {
  return useParsedQueries(
    uuids.map((uuid) => {
      return {
        queryKey: ["lizard-api-v4", "rasteralarms", uuid],
        fetchFunction: () => fetchWithError(`/api/v4/rasteralarms/${uuid}/`),
        parseFunction: (json: unknown) => rasterAlarmSchema.parse(json),
      };
    })
  );
}
